import { useEffect, useRef } from "react";
import ScrollReveal from "./ScrollReveal";
import { MapPin, Phone, Instagram, Facebook, Send, Loader2 } from "lucide-react";
import { useForm, ValidationError } from "@formspree/react";

export default function ContactSection() {
  const [state, handleSubmit] = useForm("maqaeryv");
  const formRef = useRef(null);

  useEffect(() => {
    if (state.succeeded && formRef.current) {
      formRef.current.reset();
    }
  }, [state.succeeded]);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-36 bg-[#0273DB]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <ScrollReveal>
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
              Un projet ? Parlons-en
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <ScrollReveal className="lg:col-span-2">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">Notre zone</div>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Basé à Sainte-Olive (01330)
                    <br />
                    Déplacement dans toute la région (1h autour)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">Téléphone</div>
                  <a
                    href="tel:+33695134999"
                    data-testid="contact-phone"
                    className="text-white font-semibold hover:underline"
                  >
                    06 95 13 49 99
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">Réseaux sociaux</div>
                  <div className="flex gap-3">
                    <a
                      href="https://instagram.com/vinylux"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="social-instagram"
                      className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
                    >
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </a>
                    <a
                      href="https://www.facebook.com/profile.php?id=61590669930852"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="social-facebook"
                      className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
                    >
                      <Facebook className="w-4 h-4" />
                      Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="lg:col-span-3">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white rounded-3xl p-8 lg:p-10 shadow-2xl shadow-black/10 space-y-5"
            >
              <input
                type="hidden"
                name="_subject"
                value="🔥 Nouveau lead Vinylux"
              />

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-[#0284FC]/80 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="nom"
                    required
                    data-testid="input-name"
                    placeholder="Votre nom"
                    className="w-full px-4 py-3 rounded-xl border border-[#0284FC]/15 bg-[#0284FC]/5 text-[#0284FC] placeholder-[#0284FC]/30 focus:border-[#0284FC] focus:ring-2 focus:ring-[#0284FC]/20 outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0284FC]/80 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="telephone"
                    required
                    data-testid="input-phone"
                    placeholder="06 95 13 49 99"
                    className="w-full px-4 py-3 rounded-xl border border-[#0284FC]/15 bg-[#0284FC]/5 text-[#0284FC] placeholder-[#0284FC]/30 focus:border-[#0284FC] focus:ring-2 focus:ring-[#0284FC]/20 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0284FC]/80 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  data-testid="input-email"
                  placeholder="votre@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#0284FC]/15 bg-[#0284FC]/5 text-[#0284FC] placeholder-[#0284FC]/30 focus:border-[#0284FC] focus:ring-2 focus:ring-[#0284FC]/20 outline-none transition-all text-sm"
                />
                <ValidationError
                  prefix="Email"
                  field="email"
                  errors={state.errors}
                  className="mt-2 text-sm text-red-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0284FC]/80 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  rows={4}
                  data-testid="input-message"
                  placeholder="Décrivez votre projet..."
                  className="w-full px-4 py-3 rounded-xl border border-[#0284FC]/15 bg-[#0284FC]/5 text-[#0284FC] placeholder-[#0284FC]/30 focus:border-[#0284FC] focus:ring-2 focus:ring-[#0284FC]/20 outline-none transition-all text-sm resize-none"
                />
                <ValidationError
                  prefix="Message"
                  field="message"
                  errors={state.errors}
                  className="mt-2 text-sm text-red-600"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-button"
                disabled={state.submitting}
                className="inline-flex items-center justify-center gap-2 w-full bg-[#0284FC] hover:bg-[#026ECE] disabled:opacity-60 text-white font-bold px-8 py-4 rounded-full transition-all hover:scale-105 text-base"
              >
                {state.submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Obtenir mon devis gratuit en 24h
                  </>
                )}
              </button>

              {state.succeeded && (
                <div className="text-center">
                  <div
                    data-testid="form-success"
                    className="text-sm text-green-700 font-semibold bg-green-50 rounded-xl p-4"
                  >
                    Merci, votre demande a bien été envoyée. Je vous recontacte rapidement.
                  </div>

                  <p className="text-xs text-[#0284FC] mt-3 font-medium">
                    📞 Vous pouvez aussi m’appeler directement pour une réponse immédiate
                  </p>
                </div>
              )}

              {state.errors && state.errors.length > 0 && !state.succeeded && (
                <div
                  data-testid="form-error"
                  className="text-center text-sm text-red-600 font-semibold bg-red-50 rounded-xl p-4"
                >
                  Une erreur est survenue. Vérifiez les champs puis réessayez.
                </div>
              )}
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}